/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/**
 * Content Marker Layer Management
 *
 * Handles adding/updating Mapbox marker layers for visualizing content locations.
 * Uses Mapbox's built-in GeoJSON clustering for performance with many markers.
 *
 * Marker data is derived from filtered titles, showing where content originates.
 */

import type { ApiTitle } from "@lumi/data";
import type mapboxgl from "mapbox-gl";
import { getCountryCoordinates } from "../countryCoordinates";
import { normalizeCountryName } from "../countryMapping";
import { MAPBOX_CONFIG } from "./config";

export interface MarkerData {
  /** Longitude */
  lng: number;
  /** Latitude */
  lat: number;
  /** Country name */
  country: string;
  /** Number of titles from this location */
  count: number;
  /** Small summary of titles for this marker (used in unclustered tooltips) */
  titles: Array<{
    id: string;
    name: string;
    slug?: string;
    horizontalTileUrl?: string | null;
    backdropUrlHd?: string | null;
  }>;
}

/**
 * Ensure marker layers are above country boundaries but below country labels
 * This is critical for click event handling - markers must be on top to capture clicks
 */
function ensureMarkerLayerOrdering(map: mapboxgl.Map): void {
  const markerLayerIds = ["content-markers-clusters", "content-markers-cluster-count", "content-markers-unclustered"];

  // Move all marker layers to top (above country boundaries)
  for (const layerId of markerLayerIds) {
    if (map.getLayer(layerId)) {
      try {
        map.moveLayer(layerId);
      } catch (err) {
        console.warn(`[markerLayer] failed to move ${layerId} to top`, err);
      }
    }
  }

  // Then move country-labels above markers (if present) for text readability
  if (map.getLayer("country-labels")) {
    try {
      map.moveLayer("country-labels");
    } catch (err) {
      console.warn("[markerLayer] failed to move country-labels above markers", err);
    }
  }
}

/**
 * Convert filtered titles to GeoJSON marker data
 * Creates one marker per title (not per country) so clustering can work properly
 * Distributes markers evenly in a circular pattern around each country's center
 *
 * @param titles - Filtered title list from browse page
 * @returns Array of marker data points
 */
export function titlesToMarkerData(titles: ApiTitle[]): MarkerData[] {
  const markers: MarkerData[] = [];

  // Group titles by country to arrange them evenly
  const titlesByCountry = new Map<string, ApiTitle[]>();
  for (const title of titles) {
    if (!title.country) continue;
    const normalized = normalizeCountryName(title.country);
    const existing = titlesByCountry.get(normalized) ?? [];
    existing.push(title);
    titlesByCountry.set(normalized, existing);
  }

  // Create markers with even circular distribution per country
  for (const [country, countryTitles] of titlesByCountry.entries()) {
    const coords = getCountryCoordinates(country);
    if (!coords) continue;

    const numTitles = countryTitles.length;

    // Distribute markers evenly in concentric circles
    // First marker at center, then arrange others in rings
    for (let i = 0; i < numTitles; i++) {
      const title = countryTitles[i];
      let lng = coords.lng;
      let lat = coords.lat;

      if (i > 0) {
        // Calculate ring and position within ring
        // Rings: 1-6 in first ring, 7-18 in second ring, etc.
        const markersPerRing = 6;
        const ring = Math.floor((i - 1) / markersPerRing) + 1;
        const posInRing = (i - 1) % markersPerRing;

        // Even angular distribution around the circle
        const angle = (posInRing / markersPerRing) * 2 * Math.PI;

        // Radius increases with each ring (0.2° per ring ≈ 22km)
        const radius = ring * 0.5;

        // Calculate offset using polar coordinates
        lng = coords.lng + radius * Math.cos(angle);
        lat = coords.lat + radius * Math.sin(angle);
      }

      markers.push({
        lng,
        lat,
        country,
        count: 1,
        titles: [
          {
            id: title.id,
            name: title.name,
            slug: title.slug,
            horizontalTileUrl: title.backdropUrlHd ?? null,
            backdropUrlHd: title.horizontalTileUrl ?? title.backdropUrlHd ?? null,
          },
        ],
      });
    }
  }

  return markers;
}

/**
 * Convert marker data to GeoJSON FeatureCollection
 * Format required by Mapbox GeoJSON source
 */
function markersToGeoJSON(markers: MarkerData[], filterSummary?: string): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: markers.map((marker) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [marker.lng, marker.lat],
      },
      properties: {
        country: marker.country,
        count: marker.count,
        titles: marker.titles,
        filterSummary: filterSummary ?? null,
      },
    })),
  };
}

/**
 * Add content marker layers to the map with clustering
 *
 * Creates four layers:
 * 1. Cluster circles - Visual representation of clustered markers
 * 2. Cluster labels - Number of markers in each cluster
 * 3. Unclustered markers - Individual content markers
 * 4. Marker labels - Count of titles per location
 *
 * @param map - Mapbox GL map instance
 * @param markers - Array of marker data points
 */
export function addMarkerLayer(map: mapboxgl.Map, markers: MarkerData[], filterSummary?: string): void {
  const sourceId = "content-markers";
  const clusterLayerId = "content-markers-clusters";
  const clusterCountLayerId = "content-markers-cluster-count";
  const unclusteredLayerId = "content-markers-unclustered";

  // Check if source already exists
  if (map.getSource(sourceId)) {
    // Update existing source data
    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
    source.setData(markersToGeoJSON(markers, filterSummary));
    return;
  }

  // Add GeoJSON source with clustering enabled
  map.addSource(sourceId, {
    type: "geojson",
    data: markersToGeoJSON(markers, filterSummary),
    cluster: true,
    clusterMaxZoom: 5, // Max zoom to cluster points (clusters break into individual markers at zoom 15+)
    clusterRadius: 50, // Radius of each cluster (pixels)
  });

  // Insert marker layers AFTER country boundaries so they're on top and can capture clicks
  // This prevents country-boundaries from intercepting marker click events
  // Don't use beforeLayer - add to top of the stack, then arrange order below
  const beforeLayer = undefined;

  // Layer 1: Cluster circles (larger circles with accent glow)
  // Uses zoom-reactive styling for better visibility
  map.addLayer(
    {
      id: clusterLayerId,
      type: "circle",
      source: sourceId,
      filter: ["has", "point_count"], // Only show for clusters
      paint: {
        // Circle color: accent yellow
        "circle-color": MAPBOX_CONFIG.countryBoundaries.borderHoverColor, // #F3B33B
        // Circle size scales with cluster size
        "circle-radius": [
          "step",
          ["get", "point_count"],
          15, // radius for clusters with <10 points
          10,
          20, // radius for clusters with 10-50 points
          50,
          25, // radius for clusters with 50+ points
        ],
        "circle-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          0.95, // More opaque at LOW zoom (zoomed out)
          6.5,
          0.8, // Default opacity at HIGH zoom (zoomed in)
        ],
        // Enhanced glow for better visibility
        "circle-blur": 0.3,
        // Add white stroke at low zoom for contrast
        "circle-stroke-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          2, // Visible stroke at LOW zoom (zoomed out)
          6.5,
          0, // No stroke at HIGH zoom (zoomed in)
        ],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          0.8, // Visible at LOW zoom (zoomed out)
          6.5,
          0, // Fade out at HIGH zoom (zoomed in)
        ],
      },
    },
    beforeLayer,
  );

  // Layer 2: Cluster count labels
  map.addLayer(
    {
      id: clusterCountLayerId,
      type: "symbol",
      source: sourceId,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 14,
      },
      paint: {
        "text-color": "#000000", // Black text on accent background
      },
    },
    beforeLayer,
  );

  // Layer 3: Individual markers (unclustered points)
  // Uses zoom-reactive styling for better visibility against night-lights
  map.addLayer(
    {
      id: unclusteredLayerId,
      type: "circle",
      source: sourceId,
      filter: ["!", ["has", "point_count"]], // Only show for individual points
      paint: {
        // Zoom-reactive color: white (high contrast) when zoomed out, accent when zoomed in
        "circle-color": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          "#ffffff", // White at LOW zoom (zoomed out) for maximum contrast
          6.5,
          MAPBOX_CONFIG.countryBoundaries.borderHoverColor, // Accent at HIGH zoom (zoomed in)
        ],
        // Zoom-reactive radius: SMALLER when zoomed out (world view), LARGER when zoomed in (detail view)
        "circle-radius": [
          "interpolate",
          ["exponential", 1.5],
          ["zoom"],
          2.5,
          6, // Smaller at LOW zoom (zoomed OUT - world view)
          6.5,
          12, // Larger at HIGH zoom (zoomed IN - detail view)
        ],
        "circle-opacity": 0.95,
        // Zoom-reactive stroke: thinner when zoomed out, thicker when zoomed in
        "circle-stroke-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          2, // Thinner at LOW zoom (zoomed OUT)
          6.5,
          3, // Thicker at HIGH zoom (zoomed IN) for better definition
        ],
        "circle-stroke-color": [
          "interpolate",
          ["linear"],
          ["zoom"],
          2.5,
          MAPBOX_CONFIG.countryBoundaries.borderHoverColor, // Accent stroke at LOW zoom (zoomed out)
          6.5,
          "#ffffff", // White stroke at HIGH zoom (zoomed in)
        ],
      },
    },
    beforeLayer,
  );

  // Note: No label layer for individual markers since each marker represents exactly 1 title

  // Ensure proper z-ordering (markers above boundaries, labels above markers)
  ensureMarkerLayerOrdering(map);
}

/**
 * Update marker layer data with new filtered titles
 *
 * @param map - Mapbox GL map instance
 * @param markers - New array of marker data points
 */
export function updateMarkerLayer(map: mapboxgl.Map, markers: MarkerData[], filterSummary?: string): void {
  const sourceId = "content-markers";
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;

  if (source) {
    source.setData(markersToGeoJSON(markers, filterSummary));
    // Re-ensure layer ordering after update (in case layers were re-added)
    ensureMarkerLayerOrdering(map);
  } else {
    // Source doesn't exist yet, create it
    addMarkerLayer(map, markers, filterSummary);
  }
}

/**
 * Remove marker layers from the map
 *
 * @param map - Mapbox GL map instance
 */
export function removeMarkerLayer(map: mapboxgl.Map): void {
  const sourceId = "content-markers";
  const layerIds = ["content-markers-clusters", "content-markers-cluster-count", "content-markers-unclustered"];

  // Remove all layers
  for (const layerId of layerIds) {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  }

  // Remove source
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
}

/**
 * Setup click handler for marker interactions
 * Clicking a cluster zooms in, clicking an individual marker toggles title filter
 *
 * @param map - Mapbox GL map instance
 * @param onTitleClick - Callback when an individual marker is clicked (title ID or null to clear)
 * @param selectedTitleId - Currently selected title ID (to enable toggle behavior)
 * @returns Cleanup function to remove event listeners
 */
export function setupMarkerClick(
  map: mapboxgl.Map,
  onTitleClick: (titleId: string | null) => void,
  selectedTitleId: string | null = null,
): () => void {
  const sourceId = "content-markers";
  const clusterLayerId = "content-markers-clusters";
  const unclusteredLayerId = "content-markers-unclustered";

  // Click on cluster: zoom in
  const onClusterClick = (e: mapboxgl.MapLayerMouseEvent) => {
    // Prevent event from bubbling to country-boundary layers below
    e.preventDefault();

    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];
    const clusterId = feature.properties?.cluster_id as number | undefined;

    if (clusterId === undefined) return;

    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;

    // Get cluster expansion zoom level
    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || feature.geometry?.type !== "Point") return;

      // Zoom to cluster, adding +2 to ensure cluster breaks into individual markers
      const targetZoom = zoom === undefined ? map.getZoom() + 2 : zoom + 2;

      // Use flyTo for smooth arc trajectory that syncs zoom and pan
      // This prevents the "zoom in place, then pan" effect of easeTo
      map.flyTo({
        center: feature.geometry.coordinates as [number, number],
        zoom: targetZoom,
        speed: 1.2, // Speed of flight (default 1.2 is smooth)
        curve: 1, // Arc curvature (1 = fairly direct path, less zoom out during flight)
        easing: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2), // ease-in-out quad
      });
    });
  };

  // Click on individual marker: toggle title filter
  const onMarkerClickHandler = (e: mapboxgl.MapLayerMouseEvent) => {
    // Prevent event from bubbling to country-boundary layers below
    e.preventDefault();

    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];

    // Get the title data from the marker
    const titlesData: unknown = feature.properties?.titles;
    if (!titlesData) return;

    // Parse titles array (may be stringified by Mapbox)
    let titles: Array<{ id: string; slug?: string }> = [];
    if (typeof titlesData === "string") {
      try {
        titles = JSON.parse(titlesData) as Array<{ id: string; slug?: string }>;
      } catch {
        return;
      }
    } else if (Array.isArray(titlesData)) {
      titles = titlesData as Array<{ id: string; slug?: string }>;
    }

    // Each unclustered marker should have exactly one title - toggle filter by ID
    if (titles.length > 0 && titles[0].id) {
      const clickedTitleId = titles[0].id;
      // Toggle: if clicking the same marker, clear filter; otherwise set filter
      if (selectedTitleId === clickedTitleId) {
        onTitleClick(null);
      } else {
        onTitleClick(clickedTitleId);
      }
    }
  };

  // Change cursor on hover
  const onMouseEnter = () => {
    map.getCanvas().style.cursor = "pointer";
  };

  const onMouseLeave = () => {
    map.getCanvas().style.cursor = "";
  };

  // Add event listeners
  map.on("click", clusterLayerId, onClusterClick);
  map.on("click", unclusteredLayerId, onMarkerClickHandler);
  map.on("mouseenter", clusterLayerId, onMouseEnter);
  map.on("mouseleave", clusterLayerId, onMouseLeave);
  map.on("mouseenter", unclusteredLayerId, onMouseEnter);
  map.on("mouseleave", unclusteredLayerId, onMouseLeave);

  // Return cleanup function
  return () => {
    map.off("click", clusterLayerId, onClusterClick);
    map.off("click", unclusteredLayerId, onMarkerClickHandler);
    map.off("mouseenter", clusterLayerId, onMouseEnter);
    map.off("mouseleave", clusterLayerId, onMouseLeave);
    map.off("mouseenter", unclusteredLayerId, onMouseEnter);
    map.off("mouseleave", unclusteredLayerId, onMouseLeave);
  };
}

/**
 * Setup background click handler to clear title filter when clicking outside markers
 * Does NOT clear country filter - that's handled by country layer
 *
 * @param map - Mapbox GL map instance
 * @param onBackgroundClick - Callback to clear title filter only
 * @returns Cleanup function
 */
export function setupMarkerBackgroundClick(map: mapboxgl.Map, onBackgroundClick: () => void): () => void {
  const clusterLayerId = "content-markers-clusters";
  const unclusteredLayerId = "content-markers-unclustered";

  const onClick = (e: mapboxgl.MapMouseEvent) => {
    // Check if click hit a marker or cluster
    const features = map.queryRenderedFeatures(e.point, {
      layers: [clusterLayerId, unclusteredLayerId],
    });

    // Only clear title filter if not clicking on markers/clusters
    // (Countries have their own background click handler)
    if (features.length === 0) {
      onBackgroundClick();
    }
  };

  map.on("click", onClick);

  return () => {
    map.off("click", onClick);
  };
}

/** Escape HTML special characters */
function escapeHtml(s: string): string {
  return s.split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;");
}

/** Parse titles from feature properties (may be stringified JSON) */
function parseTitlesFromProperties(titlesData: unknown): Array<{ horizontalTileUrl?: string; name: string }> | null {
  if (!titlesData) return null;

  if (typeof titlesData === "string") {
    try {
      return JSON.parse(titlesData) as Array<{ horizontalTileUrl?: string; name: string }>;
    } catch {
      return null;
    }
  }

  if (Array.isArray(titlesData)) {
    return titlesData as Array<{ horizontalTileUrl?: string; name: string }>;
  }

  return null;
}

/** Render cluster tooltip HTML */
function renderClusterTooltip(pointCount: number, filterSummary = "All filters"): string {
  const label = pointCount === 1 ? "film" : "films";
  return `<div style="font-weight:700;margin-bottom:10px;padding-right:12px;">${pointCount} ${label}</div><div style="font-size:10px;color:#F3B33B">${filterSummary}</div>`;
}

/** Render unclustered marker tooltip HTML */
function renderUnclusteredTooltip(
  titles: Array<{ horizontalTileUrl?: string; name: string }>,
  country: string,
  count: number,
): string {
  const label = count === 1 ? "film" : "films";
  const list = titles
    .map(
      (t) =>
        `<div style="display:flex;gap:12px;align-items:center"><img src="${t.horizontalTileUrl ?? ""}" style="width:auto;height:48px;object-fit:cover;border-radius:4px;padding-bottom:8px"/> <div style="font-weight:600">${escapeHtml(t.name)}</div></div>`,
    )
    .join("");
  return `<div style="font-weight:700;margin-bottom:10px;">${country}<span style="font-size:12px;font-weight:500;color:#F3B33B"> — ${count} ${label}</span></div><div style="max-width:350px">${list}</div>`;
}

/**
 * Setup tooltip for markers showing country name, count and titles (for unclustered)
 * and filter summary for clusters.
 *
 * @param map - Mapbox GL map instance
 * @returns Cleanup function
 */
export function setupMarkerTooltip(map: mapboxgl.Map): () => void {
  const unclusteredLayerId = "content-markers-unclustered";
  const clusterLayerId = "content-markers-clusters";

  const tooltip = document.createElement("div");
  tooltip.className = "mapbox-marker-tooltip";
  tooltip.style.cssText =
    "position:absolute;background:rgba(0,0,0,0.95);color:#fff;padding:8px 12px;border-radius:4px;font-size:14px;pointer-events:none;z-index:10000;display:none;border:1px solid #F3B33B;";
  map.getContainer().appendChild(tooltip);

  // Track current feature to avoid unnecessary redraws
  let currentFeatureId: string | number | null = null;

  const onMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
    const f = e.features?.[0];
    if (!f?.properties) {
      tooltip.style.display = "none";
      currentFeatureId = null;
      return;
    }

    const p = f.properties;
    const point = e.point;

    // Generate a unique ID for this feature (cluster_id or country name)
    const featureId = (p.cluster_id as number | undefined) ?? (p.country as string | undefined) ?? null;

    // Only update tooltip content if we're hovering a different feature
    const isNewFeature = featureId !== currentFeatureId;

    // Always update position
    tooltip.style.left = `${point.x + 10}px`;
    tooltip.style.top = `${point.y + 10}px`;

    // Cluster tooltip
    if (p.point_count) {
      if (isNewFeature) {
        const filterSummary = typeof p.filterSummary === "string" ? p.filterSummary : undefined;
        tooltip.innerHTML = renderClusterTooltip(p.point_count as number, filterSummary);
        currentFeatureId = featureId;
      }
      tooltip.style.display = "block";
      return;
    }

    // Unclustered tooltip - parse titles
    if (isNewFeature) {
      const titles = parseTitlesFromProperties(p.titles);

      if (!titles) {
        tooltip.style.display = "none";
        currentFeatureId = null;
        return;
      }

      if (titles.length > 0) {
        const country = (p.country ?? "") as string;
        const count = (p.count ?? titles.length) as number;
        tooltip.innerHTML = renderUnclusteredTooltip(titles, country, count);
        currentFeatureId = featureId;
        tooltip.style.display = "block";
      } else {
        tooltip.style.display = "none";
        currentFeatureId = null;
      }
    } else {
      tooltip.style.display = "block";
    }
  };

  const onMouseLeave = () => {
    tooltip.style.display = "none";
    currentFeatureId = null;
  };

  map.on("mousemove", unclusteredLayerId, onMouseMove);
  map.on("mouseleave", unclusteredLayerId, onMouseLeave);
  map.on("mousemove", clusterLayerId, onMouseMove);
  map.on("mouseleave", clusterLayerId, onMouseLeave);

  return () => {
    map.off("mousemove", unclusteredLayerId, onMouseMove);
    map.off("mouseleave", unclusteredLayerId, onMouseLeave);
    map.off("mousemove", clusterLayerId, onMouseMove);
    map.off("mouseleave", clusterLayerId, onMouseLeave);
    tooltip.remove();
  };
}
